import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchEnrichmentLogs, type EnrichmentLogEntry } from '../../services/schemaApi';

interface EnrichmentLogsTabProps {
  draftId: string;
}

const EnrichmentLogsTab: React.FC<EnrichmentLogsTabProps> = ({ draftId }) => {
  const { data, isLoading, error } = useQuery(
    ['enrichmentLogs', draftId],
    () => fetchEnrichmentLogs(draftId),
    { enabled: !!draftId },
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Failed to load enrichment logs.
      </Typography>
    );
  }

  const logs = data?.logs ?? [];

  if (logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={2}>
        No annotation logs yet. Click "Annotate Schema" to run the AI agent.
      </Typography>
    );
  }

  const totalInput = logs.reduce((s, l) => s + (l.token_usage?.prompt_tokens ?? 0), 0);
  const totalOutput = logs.reduce((s, l) => s + (l.token_usage?.completion_tokens ?? 0), 0);
  const totalTokens = logs.reduce((s, l) => s + (l.token_usage?.total_tokens ?? 0), 0);
  const totalToolCalls = logs.reduce(
    (s, l) => s + ((l.metadata?.tool_calls as any[])?.length ?? 0),
    0,
  );

  return (
    <Box>
      {/* Stats */}
      <Stack direction="row" spacing={1} mb={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Chip label={`${logs.length} chunk${logs.length !== 1 ? 's' : ''}`} size="small" />
        <Chip label={`Input: ${totalInput.toLocaleString()}`} size="small" color="info" />
        <Chip label={`Output: ${totalOutput.toLocaleString()}`} size="small" color="success" />
        <Chip label={`Total: ${totalTokens.toLocaleString()} tokens`} size="small" />
        <Chip label={`${totalToolCalls} tool calls`} size="small" color="secondary" />
      </Stack>

      {/* Log entries */}
      {logs.map((log) => (
        <EnrichmentLogCard key={log.log_id} log={log} />
      ))}
    </Box>
  );
};

const EnrichmentLogCard: React.FC<{ log: EnrichmentLogEntry }> = ({ log }) => {
  const [expanded, setExpanded] = useState(false);

  const chunkTables = (log.metadata?.chunk_tables as string[]) ?? [];
  const turnsUsed = (log.metadata?.turns_used as number) ?? 0;
  const toolCalls = (log.metadata?.tool_calls as Array<{ name: string; arguments: Record<string, any> }>) ?? [];
  const perTurn = (log.metadata?.per_turn_usage as any[]) ?? [];
  const chunkIdx = (log.metadata?.chunk_index as number) ?? 0;

  const inputTokens = log.token_usage?.prompt_tokens ?? 0;
  const outputTokens = log.token_usage?.completion_tokens ?? 0;

  const createdAt = log.created_at
    ? new Date(log.created_at).toLocaleString()
    : '';

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} variant="outlined" sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2">
            Chunk {chunkIdx + 1}
          </Typography>
          <Chip label={`${chunkTables.length} tables`} size="small" variant="outlined" />
          <Chip label={`${turnsUsed} turns`} size="small" variant="outlined" />
          <Chip label={`${toolCalls.length} tool calls`} size="small" variant="outlined" />
          <Chip label={`${inputTokens + outputTokens} tokens`} size="small" variant="outlined" />
          {createdAt && (
            <Typography variant="caption" color="text.secondary">
              {createdAt}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Tables in this chunk */}
          <Typography variant="caption" fontWeight={600} gutterBottom display="block">
            Tables: {chunkTables.join(', ')}
          </Typography>

          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            Model: {log.model_id ?? 'unknown'} | Input: {inputTokens.toLocaleString()} | Output: {outputTokens.toLocaleString()}
          </Typography>

          {/* Tool calls */}
          {toolCalls.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" fontWeight={600} gutterBottom display="block">
                Tool Calls
              </Typography>
              <Box
                component="div"
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {toolCalls.map((tc, i) => (
                  <Box key={i} mb={0.5}>
                    <Typography component="span" variant="caption" fontWeight={600} color="primary.main">
                      {tc.name}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({formatToolArgs(tc.arguments)})
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Final response */}
          {log.response_text && (
            <Box mt={1}>
              <Typography variant="caption" fontWeight={600} gutterBottom display="block">
                Summary
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {log.response_text}
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

function formatToolArgs(args: Record<string, any>): string {
  if (!args) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (typeof value === 'string') {
      parts.push(`${key}="${value.length > 60 ? value.slice(0, 57) + '...' : value}"`);
    } else if (Array.isArray(value)) {
      parts.push(`${key}=[${value.length}]`);
    } else {
      parts.push(`${key}=${JSON.stringify(value)}`);
    }
  }
  return parts.join(', ');
}

export default EnrichmentLogsTab;
